import { useCallback, useEffect, useState } from "react";
import { Box, IconButton } from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import SummarizeIcon from "@mui/icons-material/Summarize";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import "../../styles/Listing/listing-premium.css";
import { useNavigate } from "react-router-dom";
import { postRequest } from "../../api/apiServices";
import { jobSummaryList } from "../../api/apiList";
import Loading from "../../utils/Loading";
import PaginationComponent from "../../utils/PaginationComponent";
import debounce from "lodash/debounce";
import { roleBasedRedirection } from "../../utils/helper";

interface JobSummary {
  id: number;
  jobTitle: string;
  jobId: number;
}

const JobSummaryListing = () => {
  const [jobSummary, setJobSummary] = useState<JobSummary[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [loader, setLoader] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [hasPreviousPage, setHasPreviousPage] = useState(false);
  const navigate = useNavigate();

  const handleChangePage = (
    _event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handleAcceptSummary = (id: number) => {
    navigate(roleBasedRedirection(`/job-summary/${id}`));
  };

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const value = event.target.value;
    setSearchTerm(value);
    setSearchLoading(true);
    debouncedSearch(value);
  };

  const debouncedSearch = useCallback(
    debounce((searchValue: string) => {
      fetchJobSummaryList(1, rowsPerPage, searchValue);
      setSearchLoading(false);
    }, 500),
    []
  );

  const fetchJobSummaryList = async (
    page: number,
    limit: number,
    search: string
  ) => {
    setLoader(true);
    const response = await postRequest(jobSummaryList, {
      page,
      limit,
      search,
      searchColumns: ["jobTitle"],
    });
    if (response?.data?.success) {
      setJobSummary(response?.data?.data?.jobSummary);
      setCount(response?.data?.data?.count);
      setPage(response?.data?.data?.page - 1);
      setHasNextPage(response?.data?.data?.hasNextPage);
      setHasPreviousPage(response?.data?.data?.hasPreviousPage);
    }
    setLoader(false);
  };

  useEffect(() => {
    if (!searchLoading) {
      fetchJobSummaryList(page + 1, rowsPerPage, searchTerm);
    }
  }, [page, rowsPerPage]);

  return (
    <Box className="listing-page">
      <Box className="listing-hero">
        <Box className="listing-hero-inner">
          <Box>
            <Box className="listing-hero-badge">
              <SummarizeIcon sx={{ fontSize: 14 }} />
              Job Summary
            </Box>
            <h1 className="listing-hero-title">Job Summary</h1>
            <p className="listing-hero-subtitle">
              Review and manage job summary records
            </p>
          </Box>

          <Box className="listing-hero-actions">
            <Box className="listing-hero-search">
              <SearchIcon sx={{ color: "rgba(255,255,255,0.7)", fontSize: 20 }} />
              <input
                type="text"
                placeholder="Search job summaries..."
                value={searchTerm}
                onChange={handleSearchChange}
              />
            </Box>
          </Box>
        </Box>
      </Box>

      {loader ? (
        <Loading />
      ) : jobSummary && jobSummary.length > 0 ? (
        <>
          <Box className="listing-list">
            {jobSummary.map((item, index) => (
              <Box key={item.id} className="listing-card">
                <Box className="listing-card-body">
                  <Box className="listing-card-info">
                    <Box className="listing-card-title-row">
                      <Box className="listing-card-index">
                        {index + 1 + page * rowsPerPage}
                      </Box>
                      <Box>
                        <p className="listing-card-title">{item?.jobTitle}</p>
                      </Box>
                    </Box>
                  </Box>

                  <Box className="listing-actions">
                    <IconButton
                      className="listing-action-btn"
                      onClick={() => handleAcceptSummary(item?.jobId)}
                      title="Edit Summary"
                    >
                      <EditOutlinedIcon />
                    </IconButton>
                  </Box>
                </Box>
              </Box>
            ))}
          </Box>

          <Box className="listing-pagination-wrap">
            <PaginationComponent
              page={page}
              rowsPerPage={rowsPerPage}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              count={count}
              hasNextPage={hasNextPage}
              hasPreviousPage={hasPreviousPage}
            />
          </Box>
        </>
      ) : (
        <Box className="listing-empty">
          <Box className="listing-empty-icon">
            <SummarizeIcon />
          </Box>
          <h2 className="listing-empty-title">No Records Found</h2>
          <p className="listing-empty-text">
            {searchTerm
              ? `No job summaries found matching "${searchTerm}".`
              : "No job summary records yet."}
          </p>
        </Box>
      )}
    </Box>
  );
};

export default JobSummaryListing;
